import React, { useEffect, useState, useContext } from 'react'
import { useIntl } from 'react-intl'
import { Link, useHistory } from 'react-router-dom'
import { useManualQuery } from 'graphql-hooks'
import dayjs from 'dayjs'

import { App, Breadcrumb, Form, Button, Typography, Tooltip, Radio } from 'antd'
import { LoadingOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons'

import * as logger from '../../../services/logger'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthCompanySlice } from '../../../store/auth-company-slice'
import { useShouldEnableFeatures } from '../../../store/use-should-enable-features'
import { notificationStore } from '../../../context/notificationsContext/store'

import LeavePolicyForm from '../../../components/leave-policy-form'
import { ILeavePolicyBody } from '../../../components/location-leave-policies/types'

import IntlMessages from '../../../util/IntlMessages'

import { getLocationByIdLeaveTypesAllLeavePolicies, getUsersWithCustomLeaveQuota } from '../../../graphql/custom-queries'
import Api from '@vacationtracker/shared/services/api'

import { IGetLocationGeneral, IGetLocationLeaveTypesAllLeavePoliciesData, ILocationGeneralLeavePolicies } from '../../../types/locations'
import { decorateLeavePolicyWithQuotaUnits, ILeavePolicyForm, RolloverStatus } from '../../../types/leave-types'
import { ILocation } from '@vacationtracker/shared/types/location'
import { getShortestLeaveInterval } from '@vacationtracker/shared/functions/get-shortest-leave-interval'
import { getQuotaInOriginalUnits, getQuotaInSettingsUnits } from '@vacationtracker/shared/functions/get-days-or-hours'
import { AccrualTypeEnum, BroughtForwardExpirationSettings, QuotaUnits, ShortestLeaveIntervalEnum } from '@vacationtracker/shared/types/leave-policy'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { IGetUsersWithCustomQuota, IUserWithCustomQuota } from '../../../types/custom-queries'
import isNull from 'lodash/isNull'
import { getLocationWorkingHours, getAverageWorkingHoursForLocation } from '@vacationtracker/shared/functions/work-week'
import { FrontendUrls } from '../../../types/urls'
import { formatQuotaInDaysOrHours } from '@vacationtracker/shared/functions/quotas'

interface ILocationLeavePolicyPage {
  match: {
    params: {
      locationId: string
      leaveTypeId: string
    }
  }
}
type OverrideChangeType = 'hasUnlimitedDays' | 'quota'
const { Paragraph } = Typography

const LeavePoliciesPage = ({ match }: ILocationLeavePolicyPage): React.ReactElement => {
  const { notification } = App.useApp()
  const { formatMessage } = useIntl()
  const history = useHistory()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState<boolean>(false)
  const [showOverride, setShowOverride] = useState<boolean>(false)
  const [overrideChangeType, setOverrideChangeType] = useState<OverrideChangeType>('hasUnlimitedDays')
  const [cancelUserSettings, setCancelUserSettings] = useState<boolean>(false)
  const [location, setLocation] = useState<IGetLocationGeneral>()
  const [leavePolicy, setLeavePolicy] = useState<ILocationGeneralLeavePolicies>()
  const [usersWithCustomLeaveQuotas, setUsersWithCustomLeaveQuotas] = useState<IUserWithCustomQuota[]>([])
  const [defaultValue, setDefaultValue] = useState<any>()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const hourlyLeaveAccounting = Boolean(authCompany?.hourlyLeaveAccounting)
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const shouldEnableHourIntervals = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.shortestLeaveInterval)
  const shouldEnableAccruals = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.accruals)
  const shouldEnableLessThanOneHourLeave = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.lessThanOneHourLeave, false)

  const [ getLocationByIdLeaveTypesAllLeavePoliciesQuery ] = useManualQuery<IGetLocationLeaveTypesAllLeavePoliciesData, { id: string }>(getLocationByIdLeaveTypesAllLeavePolicies)
  const [ getUsersWithCustomLeaveQuotaQuery ] = useManualQuery<IGetUsersWithCustomQuota, {
    leaveTypeId: string
    locationId: string
  }>(getUsersWithCustomLeaveQuota)

  const tailLayout = {
    wrapperCol: {xs: { offset: 0, span: 12 }, md: { offset: 8, span: 12}},
  }

  const formLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 8 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 16 },
    },
  }

  const returnRolloverType = (numberOfRolloverDays, negativeBroughtForwardAllowed) => {
    if (typeof numberOfRolloverDays === 'undefined') {
      return RolloverStatus.no
    }

    if (numberOfRolloverDays >= 365) {
      return RolloverStatus.all
    }

    // In case of negative brought forward allowed and max number of brought forward days is 0 we should show limited
    if (numberOfRolloverDays > 0 || negativeBroughtForwardAllowed) {
      return RolloverStatus.limited
    }

    return RolloverStatus.no
  }

  useEffect(() => {
    fetchLocationAndSetDefaultValues(match.params.locationId)
    fetchUsersWithCustomLeaveQuota(match.params.locationId, match.params.leaveTypeId)
  }, [match.params.locationId])

  const fetchLocationAndSetDefaultValues = async (id: string) => {
    try {
      const response = await getLocationByIdLeaveTypesAllLeavePoliciesQuery({ variables: { id }})
      if (!response.data || response.error) throw response.error
      const location = response.data.getLocation
      const locationWorkingHours = getAverageWorkingHoursForLocation(location.workHours)
      const leavePolicyRaw = response.data.getLocation.leavePolicies.find(
        leavePolicy => `${match.params.locationId}#${match.params.leaveTypeId}` === leavePolicy.id
      ) as unknown as ILocationGeneralLeavePolicies
      const leavePolicy = decorateLeavePolicyWithQuotaUnits(leavePolicyRaw, hourlyLeaveAccounting)
      const broughtForwardExpirationSettings = getBroughtForwardExpirationSettings(leavePolicy, location)
      setLeavePolicy({
        ...leavePolicy,
        ...broughtForwardExpirationSettings,
      })
      setLocation(location)
      setDefaultValue({
        id: leavePolicy?.id || null,
        leaveTypeId: leavePolicy?.leaveType?.id || null,
        accrualType: leavePolicy?.accrualType || 'NONE',
        cancelUserSettings: false,
        shortestLeaveInterval: getShortestLeaveInterval(leavePolicy?.shortestLeaveInterval, leavePolicy?.allowHalfDays),
        quota: getQuotaInSettingsUnits(
          hourlyLeaveAccounting, locationWorkingHours, leavePolicy.daysPerYear as number, leavePolicy.hoursPerYear as number, leavePolicy.quotaSetIn as QuotaUnits
        ),
        maxRollover: getQuotaInSettingsUnits(
          hourlyLeaveAccounting, locationWorkingHours, leavePolicy.maxRolloverDays as number, leavePolicy.maxRolloverHours as number, leavePolicy.quotaSetIn as QuotaUnits
        ),
        hasUnlimitedDays: leavePolicy?.hasUnlimitedDays || false,
        hideLeaveType: leavePolicy?.hideLeaveType || false,
        isApprovalRequired: typeof leavePolicy?.isApprovalRequired !== 'undefined' ? leavePolicy?.isApprovalRequired : true,
        isReasonRequired: leavePolicy?.isReasonRequired || false,
        negativeBallanceAllowed: typeof leavePolicy?.negativeBallanceAllowed !== 'undefined' ? leavePolicy?.negativeBallanceAllowed : false,
        rolloverType: returnRolloverType(leavePolicy?.maxRolloverDays, leavePolicy?.negativeBroughtForwardAllowed || false),
        firstEarningDate: dayjs(leavePolicy?.firstEarningDate),
        accrualPeriod: leavePolicy?.accrualPeriodStart ? // for bi-weekly and range picker should be array
          [dayjs(leavePolicy?.accrualPeriodStart), dayjs(leavePolicy?.accrualPeriodStart).add(13, 'days')] :
          [dayjs(), dayjs().add(13, 'days')],
        allowLeaveInPast: leavePolicy?.allowLeaveInPast || false,
        ...broughtForwardExpirationSettings,
        allowAdvanceAccrualUsage: leavePolicy?.allowAdvanceAccrualUsage || false,
        pauseAccrualsWhenUserIsDeactivated: isNull(leavePolicy?.pauseAccrualsWhenUserIsDeactivated) || leavePolicy?.pauseAccrualsWhenUserIsDeactivated,
        negativeBroughtForwardAllowed: leavePolicy?.negativeBroughtForwardAllowed || false,
      })
    } catch (err) { logger.error('error fetching location', err) }
  }

  const getBroughtForwardExpirationSettings = (leavePolicy, location) => {
    if(typeof leavePolicy?.broughtForwardExpirationSettings?.enabled === 'boolean') {
      return {
        enableRolloverExpiry: leavePolicy?.broughtForwardExpirationSettings.enabled,
        rolloverExpiryAfterDays: leavePolicy?.broughtForwardExpirationSettings.afterDays,
        rolloverExpiryDay: leavePolicy?.broughtForwardExpirationSettings.day,
        rolloverExpiryMonth: leavePolicy?.broughtForwardExpirationSettings.month,
      }
    } else if (leavePolicy?.enableRolloverExpiry) {
      return {
        enableRolloverExpiry: leavePolicy?.enableRolloverExpiry,
        rolloverExpiryDay: leavePolicy?.rolloverExpiryDay,
        rolloverExpiryMonth: leavePolicy?.rolloverExpiryMonth,
        rolloverExpiryAfterDays: leavePolicy?.rolloverExpiryAfterDays,
      }
    } else if (!location?.rolloverNeverExpireDays) {
      return {
        enableRolloverExpiry: !location?.rolloverNeverExpireDays,
        rolloverExpiryDay: location?.rolloverExpiryDay,
        rolloverExpiryMonth: location?.rolloverExpiryMonth,
        rolloverExpiryAfterDays: location?.rolloverExpiryAfterDays,
      }
    }
  }

  const fetchUsersWithCustomLeaveQuota = async (locationId: string, leaveTypeId: string) => {
    const resultUsersWithCustomLeaveQuota = await getUsersWithCustomLeaveQuotaQuery({ variables: {
      leaveTypeId,
      locationId,
    }})
    if (!resultUsersWithCustomLeaveQuota.data || resultUsersWithCustomLeaveQuota.error) throw resultUsersWithCustomLeaveQuota.error
    setUsersWithCustomLeaveQuotas(resultUsersWithCustomLeaveQuota.data.getUsersWithCustomQuota.users)
  }

  const onFormChange = (change, all) => {
    if (change.hasUnlimitedDays === undefined && change.quota === undefined) {
      return
    }
    setOverrideChangeType(Object.keys(change)[0] as OverrideChangeType)
    const existingQuota = getQuotaInOriginalUnits(leavePolicy?.daysPerYear as number, leavePolicy?.hoursPerYear as number, leavePolicy?.quotaSetIn as QuotaUnits)
    const showOverrideCondition =
      leavePolicy &&
      usersWithCustomLeaveQuotas.length > 0 &&
      (existingQuota !== all.quota || leavePolicy.hasUnlimitedDays !== all.hasUnlimitedDays)
    setShowOverride(Boolean(showOverrideCondition))
  }

  const onFinish = async () => {
    try {
      const values = await form.validateFields()
      if (values.rolloverType === 'all') {
        values.maxRollover = hourlyLeaveAccounting ? 2928 : 366
      } else if (values.rolloverType === 'no') {
        values.maxRollover = 0
      } else {
        values.maxRollover = values.maxRollover || 0
      }
      delete values.rolloverType

      if (values.accrualType && values.accrualType !== AccrualTypeEnum.none) {
        values.firstEarningDate = values.firstEarningDate.format('YYYY-MM-DD')
        values.accrualPeriodStart = values.accrualPeriod[0].format('YYYY-MM-DD')
      }
      const formValuesData = values as ILeavePolicyForm
      if (typeof values.enableRolloverExpiry !== 'boolean') {
        values.enableRolloverExpiry = false
      }

      saveLeavePolicy(formValuesData)
    } catch (error) {
      logger.error(error)
    }
  }

  const saveLeavePolicy = async (data) => {
    try {
      setLoading(true)
      const response = await enableLeavePolicy(data, hourlyLeaveAccounting ? 'hours' : 'days')
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'components.locationLeavePolicy.updateInProgress' }),
        icon: (<LoadingOutlined />),
        duration: 0,
      })
      setActionNotifications([ ...actionNotifications, response.correlationId ])
      goToLocationPoliciesPage()
    } catch (error) {
      logger.error(error)
      handleErrorNotification(error)
    }
  }

  const enableLeavePolicy = async (leavePolicy, quotaSetIn: QuotaUnits) => {
    if (!location || !leavePolicy) {
      return
    }

    let shortestLeaveInterval = getShortestLeaveInterval(leavePolicy?.shortestLeaveInterval as ShortestLeaveIntervalEnum, leavePolicy?.allowHalfDays as boolean)
    if (shortestLeaveInterval === ShortestLeaveIntervalEnum.oneHour && !shouldEnableHourIntervals) {
      shortestLeaveInterval = ShortestLeaveIntervalEnum.halfDay
    } else if (
      (shortestLeaveInterval === ShortestLeaveIntervalEnum.halfHour ||
      shortestLeaveInterval === ShortestLeaveIntervalEnum.fifteenMinutes) &&
      !shouldEnableLessThanOneHourLeave
    ) {
      shortestLeaveInterval = shouldEnableHourIntervals ? ShortestLeaveIntervalEnum.oneHour : ShortestLeaveIntervalEnum.halfDay
    }

    let accrualType = leavePolicy.accrualType
    if (leavePolicy.accrualType !== AccrualTypeEnum.none && !shouldEnableAccruals) {
      accrualType = AccrualTypeEnum.none
    }

    const broughtForwardExpirationSettings: BroughtForwardExpirationSettings = {
      enabled: leavePolicy.enableRolloverExpiry,
    }
    if (broughtForwardExpirationSettings.enabled && typeof leavePolicy.rolloverExpiryAfterDays === 'number') {
      broughtForwardExpirationSettings.afterDays = leavePolicy.rolloverExpiryAfterDays
    }
    if (broughtForwardExpirationSettings.enabled && typeof leavePolicy.rolloverExpiryDay === 'number' && typeof leavePolicy.rolloverExpiryMonth === 'number') {
      broughtForwardExpirationSettings.day = leavePolicy.rolloverExpiryDay
      broughtForwardExpirationSettings.month = leavePolicy.rolloverExpiryMonth
    }

    const locationWorkingHours = getLocationWorkingHours(location)
    const quota = formatQuotaInDaysOrHours(hourlyLeaveAccounting, leavePolicy.quota as number, locationWorkingHours, false)
    const rollover = formatQuotaInDaysOrHours(hourlyLeaveAccounting, leavePolicy.maxRollover as number, locationWorkingHours, false)
    const body: ILeavePolicyBody = {
      toil: leavePolicy.toil,
      eventType: 'LEAVE_POLICY_ENABLED',
      eventGroup: 'LEAVE_POLICY',
      leaveTypeId: leavePolicy?.leaveType?.id || leavePolicy?.leaveTypeId,
      negativeBallanceAllowed: leavePolicy.negativeBallanceAllowed,
      locationId: location?.id as string,
      daysPerYear: quota.raw.days,
      hoursPerYear: quota.raw.hours,
      quotaSetIn,
      maxRolloverDays: rollover.raw.days,
      maxRolloverHours: rollover.raw.hours,
      hasUnlimitedDays: leavePolicy.hasUnlimitedDays,
      shortestLeaveInterval,
      hideLeaveType: leavePolicy.hideLeaveType,
      accrualType: accrualType,
      accrualCapRate: leavePolicy.accrualCapRate,
      isApprovalRequired: leavePolicy.isApprovalRequired,
      isReasonRequired: leavePolicy.isReasonRequired,
      cancelUserSettings,
      allowLeaveInPast: Boolean(leavePolicy.allowLeaveInPast),
      broughtForwardExpirationSettings,
      allowAdvanceAccrualUsage: Boolean(leavePolicy.allowAdvanceAccrualUsage),
      pauseAccrualsWhenUserIsDeactivated: isNull(leavePolicy?.pauseAccrualsWhenUserIsDeactivated) || leavePolicy?.pauseAccrualsWhenUserIsDeactivated,
      negativeBroughtForwardAllowed: leavePolicy.negativeBroughtForwardAllowed,
      version: 3,
    }
    if (leavePolicy.firstEarningDate) {
      body.firstEarningDate = leavePolicy.firstEarningDate
    }
    if (leavePolicy.accrualPeriodStart) {
      body.accrualPeriodStart = leavePolicy.accrualPeriodStart
    }
    if (typeof leavePolicy.applyBroughtForwardSettingsToThePastYear === 'boolean') {
      body.applyBroughtForwardSettingsToThePastYear = leavePolicy.applyBroughtForwardSettingsToThePastYear
    }
    if (leavePolicy.toil) {
      body.toilShortestInterval = leavePolicy.toilShortestInterval
      body.toilRequestsAllowedForUsers = leavePolicy.toilRequestsAllowedForUsers
      body.toilExpiration = leavePolicy.toilExpiration
      body.toilExpirationMonths = 0
      body.toilExpirationDate = undefined
      if (leavePolicy.toilExpiration) {
        leavePolicy.toilExpirationMonths === 'calendar-year'
          ? body.toilExpirationDate = dayjs().endOf('year').format('YYYY-MM-DD')
          : body.toilExpirationMonths = leavePolicy.toilExpirationMonths
      }
    }

    body.leaveTypeId = match.params.leaveTypeId

    return await Api.post('/core/event', body)
  }

  const handleErrorNotification = (error, correlationId?: string, title?: string): void => {
    const description = correlationId ?
      formatMessage({ id: 'error.notificationGeneral' }, { correlationId }) :
      error.response?.data?.message ? error.response?.data?.message : error.message ? error.message : JSON.stringify(error)
    notification.error({
      message: title ? title : formatMessage({ id: 'error.notificationGeneral' }),
      description,
      duration: 0,
    })
  }

  const goToLocationPoliciesPage = (): void => {
    history.push(`/app/settings/locations/${location?.id}/leave-policies`)
  }

  return (
    <div className='main-content'>
      <div className="main-content-header">
        <div className="main-content-header-title">
          <span><IntlMessages id="components.leavePolicyForm.editTitle" values={{ leavePolicyName: leavePolicy?.leaveType?.name, locationName: location?.name}} /></span>
        </div>
        <div className="main-content-header-breadcrumb">
          <Breadcrumb
            items={[
              {
                title: <Link to={FrontendUrls.dashboard}><IntlMessages id="sidebar.dashboard" /></Link>,
              },
              {
                title: <IntlMessages id="sidebar.section.leaveSetupLowercase" />,
              },
              {
                title: <Link to='/app/settings/locations'><IntlMessages id="app.locations" /></Link>,
              },
              {
                title: <Link to={`/app/settings/locations/${location?.id}/leave-policies`}>{location?.name}</Link>,
              },
              {
                title: <Link to='/app/settings/leave-types'>{leavePolicy?.leaveType?.name}</Link>,
              },
            ]}
          />
        </div>
      </div>
      <div className="main-content-body leave-policies-page">
        {defaultValue && <Form
          {...formLayout}
          form={form}
          name="LeavePolicyForm"
          onFinish={onFinish}
          initialValues={defaultValue}
          onValuesChange={onFormChange}
        >
          <LeavePolicyForm
            leavePolicy={leavePolicy}
            location={location as unknown as ILocation}
            form={form}
            // Since we have many cases to cover if semi-monthly could be any day of the month, 
            // we checked our database and found that, so far, all users either use the 1st or the 16th of the month. 
            // So, we decided to hardcode this part so that the period start is always either the 1st or the 16th of the month.
            yearStartDay={(leavePolicy && leavePolicy?.accrualType === AccrualTypeEnum.semiMonthly) ? 1 : location?.yearStartDay}
          />
          {showOverride && (
            <div id="override-notice">
              <Paragraph>
                <IntlMessages
                  id="components.leavePolicyForm.override.change"
                  values={{
                    unlimited: (...chunks) => overrideChangeType === 'hasUnlimitedDays' ? <strong>{chunks}</strong> : '',
                    customDays: (...chunks) => overrideChangeType === 'quota' ? <strong>{chunks}</strong> : '',
                    strong: (...chunks) => <strong>{chunks}</strong>,
                  }} />
              </Paragraph>
              <Paragraph>
                <IntlMessages
                  id="components.leavePolicyForm.override.affectedUsers"
                  values={{
                    totalUsers: usersWithCustomLeaveQuotas.length,
                    strong: (...chunks) => <strong>{chunks}</strong>,
                  }} />
                <Tooltip className='info-tooltip' title={usersWithCustomLeaveQuotas.map(s => s.name).join(', ')}><InfoCircleOutlined /></Tooltip>
              </Paragraph>
              <Paragraph>
                <WarningOutlined style={{color: '#faad14', fontSize: 'large', marginRight: 5 }} />
                <IntlMessages
                  id="components.leavePolicyForm.override.customLeaveQuotaInfo"
                  values={{
                    totalUsers: usersWithCustomLeaveQuotas.length,
                    strong: (...chunks) => <strong>{chunks}</strong>,
                  }} />
              </Paragraph>
              <Form.Item name="cancelUserSettings">
                <Radio.Group value={cancelUserSettings} onChange={() => setCancelUserSettings(!cancelUserSettings)}>
                  <Radio value={false}><IntlMessages id="components.leavePolicyForm.override.overrideEnable" /></Radio>
                  <Radio value={true} style={{ whiteSpace: 'normal', display: 'inline-flex', width: '100%' }}>
                    <IntlMessages id="components.leavePolicyForm.override.overrideDisable" />
                  </Radio>
                </Radio.Group>
              </Form.Item>
              <Paragraph>
                <IntlMessages id="components.leavePolicyForm.override.note" />
              </Paragraph>
            </div>
          )}
          <br />
          <Form.Item {...tailLayout}>
            <Button type="default" style={{ marginRight: 10 }} onClick={goToLocationPoliciesPage}>
              <IntlMessages id="app.cancel" />
            </Button>
            <Button disabled={loading} type="primary" loading={loading} htmlType="submit" data-qa='submit-notification'>
              <IntlMessages id="app.update" />
            </Button>
          </Form.Item>
        </Form>}
      </div>
    </div>
  )
}

export default LeavePoliciesPage
